/**
 * EditorDocument → SVG export merge algorithm (Tier 2 Group F sub-piece
 * #4 — SVG export half of the lossless round-trip). Mirror of
 * `psd-export.ts` for the SVG format: a pure structural walk
 * (`buildExportSvg`, no DOM) plus a bytes-out orchestrator
 * (`exportDocumentToSvg`, loads the stash via `loadSvgStash`).
 *
 * ## The merge contract (per leaf layer)
 *
 *   1. **Restored from stash** — layer has a `layerOriginById` entry
 *      from import time AND `rasterModified !== true`. The original
 *      SVG element from the re-parsed stash is reused VERBATIM with
 *      the editor's extrinsic properties (opacity, blend-mode,
 *      visibility, name) overlaid via attribute mutation. This is
 *      what preserves features the editor can't model natively
 *      (text, filters, masks, gradients, `<use>`, etc.) across the
 *      round trip.
 *
 *   2. **Editor vector** — `type: 'vector'` with no stash origin
 *      (editor-created), or with a stash origin but `rasterModified`.
 *      The wording "rasterModified" applies to image layers; on
 *      vector layers, edit-tracking is different — pen edits / shape
 *      regeneration etc. update the `VectorPath` directly, so the
 *      editor's current `VectorPath` is canonical. Emitted as a
 *      `<path>` (via `pathToSvgData`) with fill / stroke attrs.
 *
 *   3. **Editor raster** — `type: 'image'` that's editor-created OR
 *      `rasterModified === true`. The caller-supplied
 *      `layerCanvasProvider` returns the layer's current pixels;
 *      we embed them as `<image href="data:image/png;base64,...">`.
 *
 * ## Groups
 *
 * Always emit as fresh `<g>` from the editor tree (the user may have
 * reorganized layers since import). When a group node carries a
 * stash origin AND its own attributes can be lifted (the group itself
 * isn't `rasterModified`), the stash's `<g>`-level attributes are
 * spread before the editor's extrinsic overlay — preserves group
 * metadata (sodipodi:* / inkscape:* / aria-*) that the editor doesn't
 * model.
 *
 * ## Layer order
 *
 * Editor's `rootIds` / `childIds` is top-first (matches the Layers
 * panel). SVG document order is back-to-front (first child renders
 * behind). `buildExportSvg` reverses on emit so the output SVG's
 * stacking matches what the editor displayed.
 *
 * ## Document-level resources
 *
 * When `originalSvg` is supplied (a re-parsed `XMLDocument` from the
 * stash), the output's root `<svg>` carries:
 *  - The original `viewBox` / `xmlns` / `xmlns:xlink` / namespace decls
 *  - The original `<defs>` block (gradients, filters, masks, clipPaths,
 *    patterns, symbols all preserved)
 *  - The original `<style>` / `<title>` / `<desc>` / `<metadata>`
 *    (decorative + style declarations the editor never modeled)
 *
 * When `originalSvg` is null (editor-created document, or stash
 * absent), a minimal SVG shell is synthesized.
 */

import type {
  BlendMode as EditorBlendMode,
  EditorDocument,
  LayerNode,
  ShapeFillValue,
  ShapeGradientStop,
  VectorPath,
} from '../types';
import { pathToSvgData } from './vector-path';
import { loadSvgStash, type SvgNodeOrigin } from './svg-source-stash';
import { SVG_WALK_SKIP_TAGS } from './svg-common';
import { BASELINE_TO_TOP_RATIO as TEXT_BASELINE_TO_TOP_RATIO_SHARED } from './text-baseline-constants';

/** Hard cap on a single embedded raster's base64 data URI size. A
 *  document with hundreds of 4K-pixel layers embedded as PNG could
 *  otherwise produce a multi-GB string that OOMs the tab during
 *  serialization (the data URI gets concatenated into the output
 *  before being written to a Blob). 50MB per layer is generous —
 *  a typical PNG at 4K is ~5MB; only pathological content hits
 *  the limit. Pre-PR review finding (R1#9). */
const MAX_RASTER_DATA_URI_BYTES = 50 * 1024 * 1024;

/** Thrown when an embedded raster layer's encoded size exceeds the
 *  per-layer cap. The caller (orchestrator + EditorPage) surfaces
 *  this as a per-layer skip via the warnings channel rather than
 *  aborting the whole export. */
export class SvgExportRasterTooLargeError extends Error {
  constructor(
    public readonly layerId: string,
    public readonly bytes: number,
    public readonly limit: number,
  ) {
    super(
      `Raster layer ${layerId} encoded to ${(bytes / 1024 / 1024).toFixed(1)}MB, exceeding the ${limit / 1024 / 1024}MB-per-layer cap. Reduce layer dimensions or split the document.`,
    );
    this.name = 'SvgExportRasterTooLargeError';
  }
}

/** Per-layer payload returned by the text provider for native
 *  `<text>` emission (Tier 2 Group F SVG round-trip polish,
 *  PR-2 phase E). Structurally identical to the parser's output
 *  — same fields populated, same position semantics — so the
 *  round-trip is symmetrical. The caller (EditorPage's
 *  handleExportSvg) materializes this by querying the Fabric
 *  IText associated with the layer; tests inject synthetic
 *  payloads.
 *
 *  Returning `null` from the provider routes the text layer
 *  through the existing raster fallback — used when the IText
 *  carries features the editor's native-text emission can't
 *  express (arched / text-on-path / multi-line state from a
 *  Fabric IText that's been styled beyond the SVG `<text>`
 *  element's expressiveness). */
export interface SvgExportTextPayload {
  text: string;
  /** Scene-space TOP-LEFT of the text bbox. Inverse baseline
   *  adjustment to SVG `y` happens inside `emitTextLayer`. */
  x: number;
  y: number;
  settings: import('../types').TextSettings;
}

/** Per-layer payload returned by `layerCanvasProvider` for raster
 *  emission. Caller (EditorPage) materializes from a Fabric object
 *  or paint-surface; tests inject synthetic shapes. */
export interface SvgExportRasterPayload {
  /** The layer's current pixels — encoded to base64 PNG and inlined
   *  as `<image href="data:...">`. */
  canvas: HTMLCanvasElement;
  /** Top-left in document scene coordinates. */
  left: number;
  top: number;
}

export type LayerCanvasProvider = (layerId: string) => SvgExportRasterPayload | null;

/** Provider callback that materializes a text layer's content +
 *  styling for native `<text>` emission. Caller (EditorPage)
 *  queries the Fabric IText bound to `layerId` and returns the
 *  payload; tests inject synthetic shapes. Returning null
 *  fallthroughs to raster emission for that layer. */
export type LayerTextProvider = (layerId: string) => SvgExportTextPayload | null;

/** Map editor BlendMode → CSS `mix-blend-mode` value. SVG uses the
 *  same vocabulary as CSS Compositing. Inverse of the import-side
 *  `SVG_BLEND_MODE_MAP`. */
const EDITOR_TO_SVG_BLEND_MODE: Record<EditorBlendMode, string> = {
  'source-over': 'normal',
  multiply: 'multiply',
  screen: 'screen',
  overlay: 'overlay',
  darken: 'darken',
  lighten: 'lighten',
  'color-dodge': 'color-dodge',
  'color-burn': 'color-burn',
  'hard-light': 'hard-light',
  'soft-light': 'soft-light',
  difference: 'difference',
  exclusion: 'exclusion',
  hue: 'hue',
  saturation: 'saturation',
  color: 'color',
  luminosity: 'luminosity',
};

function toSvgBlendMode(mode: EditorBlendMode | undefined): string {
  if (!mode) return 'normal';
  return EDITOR_TO_SVG_BLEND_MODE[mode] ?? 'normal';
}

/** Flatten the original parsed XMLDocument's children in pre-order DFS.
 *  Index N corresponds to the SVG element the import walk assigned
 *  `svgNodeIndex: N` to — see `svg-import.ts::buildEditorDocumentFromSvgDoc`.
 *  This MUST stay aligned with the import walk's traversal; both
 *  halves consume `SVG_WALK_SKIP_TAGS` from `svg-common.ts` so a
 *  drift in one without the other can't happen. */
export function flattenSvgElementsPreorderDFS(svgRoot: Element): Element[] {
  const out: Element[] = [];
  function walk(el: Element) {
    if (SVG_WALK_SKIP_TAGS.has(el.localName.toLowerCase())) return;
    out.push(el);
    for (const child of Array.from(el.children)) walk(child);
  }
  for (const child of Array.from(svgRoot.children)) walk(child);
  return out;
}

// ---------- Editor-side helpers --------------------------------------------

/** Compose the editor's extrinsic attributes into an SVG attribute
 *  string. The `id` is sanitized to a valid SVG NMToken and
 *  deduplicated against `usedIds` (caller's running set) so multi-
 *  layer documents with name collisions still produce valid output.
 *  Empty when nothing to overlay.
 *
 *  When `layerIdToEmittedId` is supplied, the helper reads the
 *  pre-assigned SVG id from the map instead of computing a fresh
 *  one. Used by the textPath cross-reference path so a `<text>`
 *  can `href="#path-id"` BEFORE the `<path>` has been emitted —
 *  both ids are pre-walked in `preAssignLayerIds`. (PSD+SVG
 *  natives follow-up phase E.) */
function editorExtrinsicAttrs(
  node: LayerNode,
  usedIds: Set<string>,
  layerIdToEmittedId?: Map<string, string>,
): string {
  const parts: string[] = [];
  if (node.opacity !== undefined && node.opacity < 1) {
    parts.push(`opacity="${formatNumber(node.opacity)}"`);
  }
  if (node.blendMode && node.blendMode !== 'source-over') {
    parts.push(`style="mix-blend-mode: ${toSvgBlendMode(node.blendMode)}"`);
  }
  if (!node.visible) parts.push('display="none"');
  if (node.name) {
    const id = layerIdToEmittedId?.get(node.id) ?? uniqueId(nameToValidSvgId(node.name), usedIds);
    parts.push(`id="${escapeAttr(id)}"`);
  }
  return parts.length ? ' ' + parts.join(' ') : '';
}

/** Pre-walk the document tree to assign an SVG id for every named
 *  layer BEFORE the emission walk runs. Returns the
 *  editor-layer-id → SVG-id map and mutates `usedIds` with all
 *  the assigned ids.
 *
 *  Used by the textPath path so `<text href="#layer-id">` resolves
 *  to the SVG id its bound `<path>` will emit, regardless of
 *  whether the path emits before or after the text. */
function preAssignLayerIds(document: EditorDocument, usedIds: Set<string>): Map<string, string> {
  const map = new Map<string, string>();
  function visit(node: LayerNode | undefined): void {
    if (!node) return;
    if (node.name) {
      const id = uniqueId(nameToValidSvgId(node.name), usedIds);
      map.set(node.id, id);
    }
    if (node.childIds) {
      // Iterate REVERSE to match `buildLayer`'s emission order
      // (top-first → SVG back-to-front via `for (i = len-1; i >= 0; i--)`).
      // Forward iteration here while emission reverses would
      // assign id suffixes in opposite order for sibling name
      // collisions, breaking textPath cross-references. (Pre-PR
      // review R1 Major.)
      for (let i = node.childIds.length - 1; i >= 0; i--) {
        visit(document.layers[node.childIds[i]]);
      }
    }
  }
  for (let i = document.rootIds.length - 1; i >= 0; i--) {
    visit(document.layers[document.rootIds[i]]);
  }
  return map;
}

/** Ensure an SVG id is unique within the document by appending a
 *  numeric suffix on collision. Mutates the `usedIds` set with the
 *  final value. */
function uniqueId(candidate: string, usedIds: Set<string>): string {
  if (!usedIds.has(candidate)) {
    usedIds.add(candidate);
    return candidate;
  }
  let n = 2;
  while (usedIds.has(`${candidate}_${n}`)) n++;
  const id = `${candidate}_${n}`;
  usedIds.add(id);
  return id;
}

function formatNumber(n: number): string {
  // Compact form — same convention as `pathToSvgData`'s formatter.
  return Number(n.toFixed(3)).toString();
}

function escapeAttr(value: string): string {
  return value
    .replace(/&/g, '&amp;')
    .replace(/"/g, '&quot;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;');
}

/** Convert a freeform layer name into a valid SVG `id` attribute
 *  value. SVG ids must conform to XML's Name production: start with
 *  a letter / underscore / colon, contain only NameChars after.
 *  Layer names commonly start with digits ("42-layer", "Layer 1")
 *  or contain spaces — both invalid. Strategy: prefix with a letter
 *  if necessary, then replace invalid characters with underscores.
 *  Pre-PR review finding (R1#8). */
function nameToValidSvgId(name: string): string {
  // Strip everything that isn't a valid NameChar, replace with `_`.
  let id = name.replace(/[^A-Za-z0-9_\-:.]/g, '_');
  // Prefix with `L_` if the result doesn't start with a valid
  // NameStartChar (digits / hyphens / dots are disallowed at the
  // start by the XML Name production).
  if (id === '' || /^[^A-Za-z_:]/.test(id)) id = `L_${id}`;
  return id;
}

// ---------- Per-layer emitters --------------------------------------------

/** Per-export accumulator for editor-generated gradient `<defs>`.
 *  Each vector layer with a `ShapeFillValue` fill registers a
 *  unique paint-server id here; `serialize()` emits the
 *  consolidated `<defs>` block prepended to the output body.
 *
 *  Editor-canonical means we always emit fresh defs from the
 *  current `ShapeFillValue` rather than reusing stash gradient
 *  ids — the user's color edits land verbatim. A future
 *  optimization could detect "unchanged from stash" gradients
 *  and skip re-emission, but the round-trip remains semantically
 *  correct without it (Tier 2 Group F SVG round-trip polish, PR-2). */
interface GradientDefCollector {
  /** Register a gradient fill, returning the id to put inside
   *  `fill="url(#id)"`. Same fill registered twice produces two
   *  ids — gradient defs are not deduplicated in v1 (rare in
   *  practice; the cost is a few extra bytes in the output). */
  add(fill: ShapeFillValue, layerNode: LayerNode): string;
  /** Returns the consolidated `<defs>` markup, or an empty string
   *  when no gradients were registered. Caller prepends this to
   *  the document body INSIDE the root `<svg>` shell, alongside
   *  any stash-restored `<defs>`. */
  serialize(): string;
}

function createGradientDefCollector(usedIds: Set<string>): GradientDefCollector {
  const fragments: string[] = [];
  return {
    add(fill, layerNode) {
      // Per-layer id namespace keeps gradients tied visually to
      // their consuming layer. `uniqueId` reserves the id against
      // the same usedIds set the layer ids use, so collision with
      // a layer's own id can't happen. The `_fill` suffix
      // disambiguates from the layer's own id when layer-id and
      // gradient-id happen to overlap with the user's existing
      // structure.
      const baseId = uniqueId(nameToValidSvgId(`${layerNode.name || layerNode.id}_fill`), usedIds);
      fragments.push(buildGradientDefXml(fill, baseId));
      return baseId;
    },
    serialize() {
      if (fragments.length === 0) return '';
      return `<defs>${fragments.join('')}</defs>`;
    },
  };
}

/** Serialize a `ShapeFillValue` gradient to a `<linearGradient>`
 *  or `<radialGradient>` element. Stops are emitted with
 *  `stop-color="..."` — if the color carries an 8-digit hex
 *  alpha, we split it into `stop-color` (6-digit) + `stop-opacity`
 *  so downstream SVG renderers without `#rrggbbaa` support still
 *  honor the transparency. */
function buildGradientDefXml(fill: ShapeFillValue, id: string): string {
  if (fill.type === 'solid') {
    // Solid fills don't need a def — caller shouldn't have hit
    // us. Return empty to be defensive.
    return '';
  }
  const stopsXml = fill.stops.map(serializeGradientStop).join('');
  if (fill.type === 'linear') {
    // Convert the editor's `angle` (degrees, clockwise from +x)
    // back into x1/y1/x2/y2 endpoints across the element bbox.
    // We use `objectBoundingBox` units (default) — endpoints are
    // 0..1 relative to the element. The unit-circle vector at the
    // angle becomes the gradient direction; we map (0..1) to (0,0)
    // … (1,1) by:
    //
    //   - angle 0°  → x1=0,y1=0 ; x2=1,y2=0 (left-to-right)
    //   - angle 90° → x1=0,y1=0 ; x2=0,y2=1 (top-to-bottom)
    //
    // SVG's gradientUnits=objectBoundingBox maps these to the
    // element's bbox. Endpoint values can land slightly outside
    // [0,1] for non-axis-aligned angles (e.g. 45° → x2=0.707,
    // y2=0.707) — SVG renderers accept this and extend the
    // gradient axis appropriately.
    const rad = (fill.angle * Math.PI) / 180;
    const x2 = +Math.cos(rad).toFixed(6);
    const y2 = +Math.sin(rad).toFixed(6);
    return `<linearGradient id="${escapeAttr(id)}" x1="0" y1="0" x2="${x2}" y2="${y2}">${stopsXml}</linearGradient>`;
  }
  // radial — the editor's model has no cx/cy/r; defaults to
  // centered-in-bbox (50% / 50% / 50%) match the editor's runtime
  // rendering and SVG's default values.
  return `<radialGradient id="${escapeAttr(id)}">${stopsXml}</radialGradient>`;
}

/** Serialize a single stop. Splits any color with embedded alpha
 *  (8-digit hex `#rrggbbaa`, `rgba(r, g, b, a)`) into stop-color
 *  (alpha-free) + an explicit stop-opacity. Some SVG renderers
 *  (Inkscape, older browsers) don't honor `#rrggbbaa` or `rgba()`
 *  inside `stop-color`; splitting keeps the output universally
 *  consumable. (Pre-PR review Major #4 — earlier draft emitted
 *  `stop-color="rgba(...)"` verbatim, which fails to render in
 *  strict viewers.) */
function serializeGradientStop(stop: ShapeGradientStop): string {
  const offset = formatNumber(stop.offset);
  const trimmed = stop.color.trim();
  // 8-digit hex: split into 6-digit color + opacity.
  const hex8 = /^#([0-9a-fA-F]{6})([0-9a-fA-F]{2})$/.exec(trimmed);
  if (hex8) {
    const baseColor = `#${hex8[1]}`;
    const alphaInt = parseInt(hex8[2], 16);
    const alpha = +(alphaInt / 255).toFixed(4);
    return `<stop offset="${offset}" stop-color="${escapeAttr(baseColor)}" stop-opacity="${alpha}" />`;
  }
  // rgba(r, g, b, a): split into rgb() + stop-opacity. Tolerate
  // both `rgba` and `rgb()` with 4 args.
  const rgba = /^rgba?\s*\(\s*([^,)]+)\s*,\s*([^,)]+)\s*,\s*([^,)]+)\s*,\s*([^)]+)\s*\)$/i.exec(
    trimmed,
  );
  if (rgba) {
    const baseColor = `rgb(${rgba[1].trim()}, ${rgba[2].trim()}, ${rgba[3].trim()})`;
    const alpha = +parseFloat(rgba[4]).toFixed(4);
    if (!Number.isNaN(alpha)) {
      return `<stop offset="${offset}" stop-color="${escapeAttr(baseColor)}" stop-opacity="${alpha}" />`;
    }
  }
  return `<stop offset="${offset}" stop-color="${escapeAttr(stop.color)}" />`;
}

/** Ratio of baseline-to-top distance over font-size. Re-exported
 *  alias for the shared `BASELINE_TO_TOP_RATIO` from
 *  `text-baseline-constants.ts`; the local alias preserves
 *  readability at the call site. */
const TEXT_BASELINE_TO_TOP_RATIO = TEXT_BASELINE_TO_TOP_RATIO_SHARED;

/** Emit a `<text>` element from a text layer's payload. Inverse
 *  of `svg-text-import.ts::parseSvgTextElement`. Symmetrical
 *  attribute mapping: TextSettings → SVG attribute / inline
 *  style. Tier 2 Group F SVG round-trip polish (PR-2 phase E). */
function emitTextLayer(
  node: LayerNode,
  payload: SvgExportTextPayload,
  usedIds: Set<string>,
  layerIdToEmittedId: Map<string, string>,
): string {
  const s = payload.settings;
  // y conversion: editor top-left → SVG baseline-y.
  const baselineY = payload.y + s.fontSize * TEXT_BASELINE_TO_TOP_RATIO;

  const attrs: string[] = [];
  attrs.push(`x="${formatNumber(payload.x)}"`);
  attrs.push(`y="${formatNumber(baselineY)}"`);
  attrs.push(`font-family="${escapeAttr(s.fontFamily)}"`);
  attrs.push(`font-size="${formatNumber(s.fontSize)}"`);
  if (s.fontWeight === 'bold') attrs.push('font-weight="bold"');
  if (s.fontStyle === 'italic') attrs.push('font-style="italic"');
  if (s.underline) attrs.push('text-decoration="underline"');
  // textAlign: editor's left/center/right → SVG's start/middle/end.
  const anchor = s.textAlign === 'center' ? 'middle' : s.textAlign === 'right' ? 'end' : null;
  if (anchor) attrs.push(`text-anchor="${anchor}"`);
  // Fill: `transparent` → `fill="none"` (SVG-canonical); solid
  // color string emits verbatim. Gradient fills aren't possible
  // here — the editor's TextSettings.fill is `string`-only, so
  // the text provider only ever returns a string.
  if (s.fill === 'transparent') {
    attrs.push('fill="none"');
  } else {
    attrs.push(`fill="${escapeAttr(s.fill)}"`);
  }
  // letter-spacing: editor stores thousandths-of-em; convert
  // back to px (fontSize × em-fraction).
  if (s.letterSpacing && s.letterSpacing !== 0) {
    const px = (s.letterSpacing / 1000) * s.fontSize;
    attrs.push(`letter-spacing="${formatNumber(px)}"`);
  }
  // Stroke: emit when present + non-zero. `position` doesn't have
  // a direct SVG analog (SVG always strokes centered); inside /
  // outside fidelity is lost on export. Round-trip preserves
  // round-tripped strokes back to 'center' on subsequent import.
  if (s.stroke && s.stroke.width > 0) {
    attrs.push(`stroke="${escapeAttr(s.stroke.color)}"`);
    attrs.push(`stroke-width="${formatNumber(s.stroke.width)}"`);
  }
  // Drop shadow: emit as inline style `filter: drop-shadow(...)`.
  // Compose with mix-blend-mode if both are present — the
  // extrinsic-attr helper handles blend mode separately, so we
  // need to merge here.
  const styleParts: string[] = [];
  if (s.dropShadow) {
    const ds = s.dropShadow;
    styleParts.push(
      `filter: drop-shadow(${formatNumber(ds.offsetX)}px ${formatNumber(ds.offsetY)}px ${formatNumber(ds.blur)}px ${ds.color})`,
    );
  }
  // Compose attrs + the editor's extrinsic attrs. extrinsicAttrs
  // emits opacity / mix-blend-mode style / display / id. When we
  // already have a filter style, splice it into the style attr
  // the helper produces.
  const extrinsic = editorExtrinsicAttrs(node, usedIds, layerIdToEmittedId);
  let finalAttrs = `${attrs.join(' ')}${extrinsic}`;
  if (styleParts.length > 0) {
    // Insert / merge with existing style attr.
    if (/style="/.test(finalAttrs)) {
      finalAttrs = finalAttrs.replace(/style="([^"]*)"/, (_full, existing: string) => {
        const merged = existing
          ? `${existing.trim().replace(/;\s*$/, '')}; ${styleParts.join('; ')}`
          : styleParts.join('; ');
        return `style="${merged}"`;
      });
    } else {
      finalAttrs += ` style="${styleParts.join('; ')}"`;
    }
  }
  // Text-on-path — emit as `<text>...<textPath href="#pathId">...</textPath></text>`
  // when the layer has a TextPathBinding. PSD+SVG natives
  // follow-up phase E. Multi-line text-on-path is rare and falls
  // through to single-line content (SVG renders the bound text
  // sequentially along the path).
  //
  // ID resolution: the bound layer's SVG id was pre-assigned by
  // `preAssignLayerIds` before this emission walk started.
  // Reading from `layerIdToEmittedId` guarantees the href matches
  // the path's actual emitted id, regardless of emission order.
  if (node.textPath) {
    const pathRef = layerIdToEmittedId.get(node.textPath.pathLayerId);
    if (pathRef) {
      const safeContent = escapeXmlText(payload.text);
      return `<text ${finalAttrs}><textPath href="#${escapeAttr(pathRef)}">${safeContent}</textPath></text>`;
    }
    // Bound path not found in the document (deleted, or never
    // imported) — fall through to plain text emission so the
    // glyphs at least render somewhere. Loss of the on-path
    // styling is the expected trade-off.
  }
  // Multi-line text → emit one `<tspan>` per line, with `dy`
  // offsets driving the line-break positioning. SVG `<text>`
  // doesn't render literal `\n` in its content — text is laid
  // out as a single line unless explicit line-break tspans are
  // used. Fabric IText handles `\n` natively; we restore the
  // semantics on export via tspans. (Phase D of PSD+SVG natives
  // follow-up — round-trip polish for `<tspan>`-driven multi-line.)
  const lines = payload.text.split('\n');
  if (lines.length > 1) {
    const x = formatNumber(payload.x);
    const tspans = lines.map((line, i) => {
      // First line gets `dy="0"` (sits at the baseline-y on the
      // outer `<text>`). Subsequent lines shift by lineHeight em,
      // matching Fabric IText's per-line spacing math.
      const dy = i === 0 ? '0' : `${formatNumber(s.lineHeight)}em`;
      return `<tspan x="${x}" dy="${dy}">${escapeXmlText(line)}</tspan>`;
    });
    return `<text ${finalAttrs}>${tspans.join('')}</text>`;
  }
  const safeContent = escapeXmlText(payload.text);
  return `<text ${finalAttrs}>${safeContent}</text>`;
}

/** Escape text content for XML serialization. The text provider
 *  hands us the raw string; we must convert `<`, `>`, `&` so the
 *  output remains well-formed XML. */
function escapeXmlText(s: string): string {
  return s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
}

/** Emit a `<path>` element from an editor vector layer's `VectorPath`.
 *  Reuses `pathToSvgData` for d-string formation (lossless inverse
 *  of the import-side parser).
 *
 *  When `path.fill` is a `ShapeFillValue` gradient, registers a
 *  `<linearGradient>` / `<radialGradient>` def via the collector
 *  and emits `fill="url(#layer-N-fill)"`. Solid string fills emit
 *  inline as before. */
function emitVectorLayer(
  node: LayerNode,
  path: VectorPath,
  usedIds: Set<string>,
  gradientCollector: GradientDefCollector,
  layerIdToEmittedId: Map<string, string>,
): string {
  const d = pathToSvgData(path);
  const fillAttr = resolveVectorFillAttr(path.fill, node, gradientCollector);
  const strokeAttr = path.stroke
    ? ` stroke="${escapeAttr(path.stroke.color)}" stroke-width="${formatNumber(path.stroke.width)}"${
        path.stroke.cap ? ` stroke-linecap="${path.stroke.cap}"` : ''
      }${path.stroke.join ? ` stroke-linejoin="${path.stroke.join}"` : ''}`
    : '';
  return `<path d="${d}"${fillAttr}${strokeAttr}${editorExtrinsicAttrs(node, usedIds, layerIdToEmittedId)} />`;
}

/** Resolve a vector path's `fill` slot to the right SVG attribute
 *  fragment. Handles all three shapes — string (solid color),
 *  `{ type: 'solid', color }`, gradient ShapeFillValue — and the
 *  `transparent` / `none` keyword. */
function resolveVectorFillAttr(
  fill: VectorPath['fill'],
  node: LayerNode,
  gradientCollector: GradientDefCollector,
): string {
  if (typeof fill === 'string') {
    if (fill === 'transparent') return ' fill="none"';
    return ` fill="${escapeAttr(fill)}"`;
  }
  if (!fill) return '';
  if (fill.type === 'solid') {
    return ` fill="${escapeAttr(fill.color)}"`;
  }
  // Gradient fill — register the def and reference via url(#id).
  const id = gradientCollector.add(fill, node);
  return ` fill="url(#${escapeAttr(id)})"`;
}

/** Emit an `<image>` element with the layer's pixels inlined as a
 *  base64-encoded PNG data URI. Position from the payload's
 *  `left`/`top`; dimensions from the canvas. Throws
 *  `SvgExportRasterTooLargeError` when the encoded data URI exceeds
 *  the per-layer cap — the caller's loop catches and records the
 *  layer as skipped. Pre-PR review finding (R1#9). */
function emitRasterLayer(
  node: LayerNode,
  payload: SvgExportRasterPayload,
  usedIds: Set<string>,
  layerIdToEmittedId: Map<string, string>,
): string {
  const dataUri = payload.canvas.toDataURL('image/png');
  if (dataUri.length > MAX_RASTER_DATA_URI_BYTES) {
    throw new SvgExportRasterTooLargeError(node.id, dataUri.length, MAX_RASTER_DATA_URI_BYTES);
  }
  return `<image x="${formatNumber(payload.left)}" y="${formatNumber(
    payload.top,
  )}" width="${payload.canvas.width}" height="${payload.canvas.height}" href="${escapeAttr(
    dataUri,
  )}"${editorExtrinsicAttrs(node, usedIds, layerIdToEmittedId)} />`;
}

// ---------- Stash-restore path --------------------------------------------

/** Apply the editor's extrinsic attributes to a (cloned) SVG element
 *  in-place. Shared between leaf-layer stash-restore and group
 *  stash-restore so the overlay semantics stay identical. */
function applyEditorExtrinsicToElement(
  el: Element,
  node: LayerNode,
  usedIds: Set<string>,
  layerIdToEmittedId?: Map<string, string>,
): void {
  if (node.opacity !== undefined) {
    el.setAttribute('opacity', formatNumber(node.opacity));
  } else {
    el.removeAttribute('opacity');
  }
  // Preserve existing style declarations but reset mix-blend-mode.
  const currentStyle = el.getAttribute('style') ?? '';
  const styleWithoutBlend = currentStyle
    .split(';')
    .map((s) => s.trim())
    .filter((s) => s !== '' && !s.startsWith('mix-blend-mode'))
    .join('; ');
  const styleParts: string[] = [];
  if (styleWithoutBlend) styleParts.push(styleWithoutBlend);
  if (node.blendMode && node.blendMode !== 'source-over') {
    styleParts.push(`mix-blend-mode: ${toSvgBlendMode(node.blendMode)}`);
  }
  if (styleParts.length) {
    el.setAttribute('style', styleParts.join('; '));
  } else {
    el.removeAttribute('style');
  }
  if (node.visible) {
    el.removeAttribute('display');
  } else {
    el.setAttribute('display', 'none');
  }
  if (node.name) {
    // Prefer the pre-assigned id from the layerIdToEmittedId map
    // when supplied — the stash-restore path overlays on a cloned
    // element whose `id` attribute is about to be set; using the
    // pre-walked id guarantees consistency with any other ref
    // (textPath, gradient-fill ref) that targets this layer.
    const id = layerIdToEmittedId?.get(node.id) ?? uniqueId(nameToValidSvgId(node.name), usedIds);
    el.setAttribute('id', id);
  }
}

/** Serialize an SVG element verbatim, then surgically overlay the
 *  editor's extrinsic attributes. The stash element keeps its
 *  feature-rich content (text data, filter refs, gradient fills,
 *  mask refs, etc.); only the user-controllable extrinsics get the
 *  editor's current values. */
function restoreFromStashWithOverlay(
  node: LayerNode,
  stashEl: Element,
  usedIds: Set<string>,
  layerIdToEmittedId: Map<string, string>,
): string {
  const cloned = stashEl.cloneNode(true) as Element;
  applyEditorExtrinsicToElement(cloned, node, usedIds, layerIdToEmittedId);
  return new XMLSerializer().serializeToString(cloned);
}

// ---------- buildExportSvg orchestrator -----------------------------------

export interface BuildExportSvgOptions {
  document: EditorDocument;
  /** Re-parsed original SVG (from `new DOMParser().parseFromString(stash.svgText, 'image/svg+xml')`).
   *  When null, every layer emits as editor-side raster / vector —
   *  no features can be restored from the original. */
  originalSvg: XMLDocument | null;
  /** Per-layer origin map from the stash. When null or missing an
   *  entry for a given LayerNode.id, that layer emits from the editor
   *  side. */
  layerOriginById: Record<string, SvgNodeOrigin> | null;
  /** Resolves a raster layer's current pixels. Returns null when
   *  the layer has no pixel content; the walk skips it. */
  layerCanvasProvider: LayerCanvasProvider;
  /** Resolves a text layer to a native `<text>` payload. Optional
   *  — when omitted (or returns null for a given layer), text
   *  layers fall through to the raster-emit path the way they
   *  did before PR-2. EditorPage wires this through by querying
   *  the Fabric IText bound to the layer; tests inject
   *  synthetic shapes. */
  layerTextProvider?: LayerTextProvider;
  /** Canvas dimensions = SVG `width` / `height` / `viewBox`. */
  canvasWidth: number;
  canvasHeight: number;
}

/** Per-layer outcome summary, surfaced via `buildExportSvgResult.summary`
 *  so the caller can show a pre-save preview ("3 layers preserved
 *  from original SVG; 2 saved as raster"). */
export interface SvgExportSummary {
  preservedFromStash: number;
  emittedAsVector: number;
  emittedAsRaster: number;
  /** Editor-created (no stash origin) — subset of vector + raster. */
  editorCreated: number;
  /** Stash-backed layers whose `rasterModified` flag forced re-emission
   *  — subset of `emittedAsRaster`. */
  rasterModified: number;
  /** Layers with no pixel content + no stash origin, skipped entirely. */
  skipped: number;
}

export interface BuildExportSvgResult {
  /** SVG document as a UTF-8 string, ready for `Blob`/download. */
  svgText: string;
  summary: SvgExportSummary;
}

/** Pure structural walk — produces the output SVG text plus a
 *  summary of per-layer emission outcomes. */
export function buildExportSvg(opts: BuildExportSvgOptions): BuildExportSvgResult {
  const {
    document,
    originalSvg,
    layerOriginById,
    layerCanvasProvider,
    layerTextProvider,
    canvasWidth,
    canvasHeight,
  } = opts;

  // Build a flat preorder array of the stash's elements so we can
  // index into it via `svgNodeIndex`. Walk must mirror the import's
  // skip list — see SVG_WALK_SKIP_TAGS in svg-common.ts.
  let stashFlat: Element[] = [];
  if (originalSvg && originalSvg.documentElement) {
    stashFlat = flattenSvgElementsPreorderDFS(originalSvg.documentElement);
  }

  const summary: SvgExportSummary = {
    preservedFromStash: 0,
    emittedAsVector: 0,
    emittedAsRaster: 0,
    editorCreated: 0,
    rasterModified: 0,
    skipped: 0,
  };

  // Tracks ids emitted into the output. Layer names are sanitized
  // to valid SVG NMTokens and deduplicated via numeric suffix on
  // collision (R1#8). Initialized empty per export — a future tab
  // export starts fresh.
  const usedIds = new Set<string>();

  // Pre-walk every layer to assign its SVG `id` BEFORE the
  // emission walk runs. Required for `<textPath>` cross-references:
  // a text layer's `href="#path-id"` must resolve to the SVG id
  // its bound `<path>` will emit, regardless of which one emits
  // first. (PSD+SVG natives follow-up phase E.)
  const layerIdToEmittedId = preAssignLayerIds(document, usedIds);

  // Per-export gradient `<defs>` accumulator — see
  // GradientDefCollector docstring for why we re-emit on every
  // export instead of reusing stash gradient ids. (Tier 2 Group F
  // SVG round-trip polish, PR-2.)
  const gradientCollector = createGradientDefCollector(usedIds);

  function buildLayer(node: LayerNode): string | null {
    // Group: recurse children in reverse (editor top-first → SVG
    // back-to-front). Preserve stash-origin group's own metadata
    // attributes if available.
    if (node.type === 'group') {
      const childIds = node.childIds ?? [];
      const childParts: string[] = [];
      for (let i = childIds.length - 1; i >= 0; i--) {
        const child = document.layers[childIds[i]];
        if (!child) continue;
        const childSvg = buildLayer(child);
        if (childSvg) childParts.push(childSvg);
      }
      const origin = layerOriginById?.[node.id];
      const stashEl = origin ? stashFlat[origin.svgNodeIndex] : undefined;
      // For stash-backed groups, lift the original group's attributes
      // (sodipodi:* / inkscape:label / aria-* / namespace decls)
      // via DOM ops rather than string surgery: clone shallow, apply
      // the editor's extrinsic overlay, then build the open + close
      // tags manually around the (already-serialized) children.
      // Earlier versions used regex-based string manipulation on the
      // serializer's output, which broke when the serializer emitted
      // `</g>` for an empty element instead of `/>`. Pre-PR review
      // finding (R1#6 / R2#2).
      if (stashEl && stashEl.localName.toLowerCase() === 'g') {
        summary.preservedFromStash += 1;
        const cloned = stashEl.cloneNode(false) as Element;
        applyEditorExtrinsicToElement(cloned, node, usedIds, layerIdToEmittedId);
        // Serialize the cloned (empty) <g> to extract its attribute
        // list. The serializer may emit either `<g attrs/>` or
        // `<g attrs></g>` — both forms expose the same attribute
        // span between `<g ` and the first `/>` or `>`.
        const emptyTag = new XMLSerializer().serializeToString(cloned);
        const attrSpan = extractGroupAttrSpan(emptyTag);
        return `<g${attrSpan}>${childParts.join('')}</g>`;
      }
      // No stash-origin group — synthesize.
      return `<g${editorExtrinsicAttrs(node, usedIds, layerIdToEmittedId)}>${childParts.join('')}</g>`;
    }

    // Adjustment layers — no native SVG equivalent. Skip (the editor's
    // tree position in the canvas was already rendered into the
    // underlying layers' pixels when adjustments commit destructively;
    // non-destructive adjustments are a runtime composite that doesn't
    // round-trip to SVG. Same treatment as the PSD path's filter-*
    // adjustment kinds — see psd-adjustment-map.ts:387.).
    if (node.type === 'adjustment') {
      summary.skipped += 1;
      return null;
    }

    // Vector layer — editor-canonical via VectorPath. (Stash origin
    // is informational only; the editor's path is authoritative
    // because pen edits update it directly without a rasterModified
    // flag.) The summary counts vectors as their own category, NEVER
    // as preservedFromStash — that bucket is reserved for layers
    // whose content was restored verbatim from the source-stash
    // (only image-type layers qualify because vectors always
    // re-emit from the editor's path). Pre-PR review finding (R1#1)
    // — earlier versions incremented both buckets, producing
    // misleading toast counts.
    if (node.type === 'vector' && node.path) {
      const origin = layerOriginById?.[node.id];
      summary.emittedAsVector += 1;
      if (!origin) summary.editorCreated += 1;
      return emitVectorLayer(node, node.path, usedIds, gradientCollector, layerIdToEmittedId);
    }

    // Image layer — decide stash-restore vs raster-emit.
    if (node.type === 'image') {
      const origin = layerOriginById?.[node.id];
      const stashEl = origin ? stashFlat[origin.svgNodeIndex] : undefined;
      const canRestoreFromStash = stashEl && !node.rasterModified && originalSvg;
      if (canRestoreFromStash) {
        summary.preservedFromStash += 1;
        return restoreFromStashWithOverlay(node, stashEl, usedIds, layerIdToEmittedId);
      }
      const payload = layerCanvasProvider(node.id);
      if (!payload) {
        summary.skipped += 1;
        return null;
      }
      try {
        const xml = emitRasterLayer(node, payload, usedIds, layerIdToEmittedId);
        summary.emittedAsRaster += 1;
        if (origin && node.rasterModified) summary.rasterModified += 1;
        else if (!origin) summary.editorCreated += 1;
        return xml;
      } catch (err) {
        if (err instanceof SvgExportRasterTooLargeError) {
          summary.skipped += 1;
          return null;
        }
        throw err;
      }
    }

    // Text layer — try native first (Tier 2 Group F SVG round-trip
    // polish, PR-2 phase E). When `layerTextProvider` is wired AND
    // returns a payload for this layer, emit a `<text>` element
    // instead of rasterizing. Returning null falls through to the
    // raster path — used when the provider's IText carries
    // arch / text-on-path state that the native emitter can't
    // express.
    if (node.type === 'text' && layerTextProvider) {
      const textPayload = layerTextProvider(node.id);
      if (textPayload) {
        const origin = layerOriginById?.[node.id];
        summary.emittedAsVector += 1;
        if (!origin) summary.editorCreated += 1;
        return emitTextLayer(node, textPayload, usedIds, layerIdToEmittedId);
      }
    }

    // Text / shape / paint / empty layer types — editor-only kinds.
    // Same fallback as image: raster emission via provider, or skip.
    const payload = layerCanvasProvider(node.id);
    if (!payload) {
      summary.skipped += 1;
      return null;
    }
    try {
      const xml = emitRasterLayer(node, payload, usedIds, layerIdToEmittedId);
      summary.emittedAsRaster += 1;
      summary.editorCreated += 1;
      return xml;
    } catch (err) {
      if (err instanceof SvgExportRasterTooLargeError) {
        summary.skipped += 1;
        return null;
      }
      throw err;
    }
  }

  // Reverse rootIds: editor top-first → SVG back-to-front. The
  // first child of `<svg>` renders BEHIND subsequent children.
  const bodyParts: string[] = [];
  for (let i = document.rootIds.length - 1; i >= 0; i--) {
    const node = document.layers[document.rootIds[i]];
    if (!node) continue;
    const layer = buildLayer(node);
    if (layer) bodyParts.push(layer);
  }
  const body = bodyParts.join('');

  // Compose document-level shell. When we have an originalSvg,
  // preserve its root attributes (viewBox / namespaces) and its
  // `<defs>` / `<style>` / `<title>` etc. siblings — the editor's
  // tree only contains layers, so non-layer content lives entirely
  // in the stash. Editor-generated gradient defs (from vector
  // layers with `ShapeFillValue` fills) get their own `<defs>`
  // block prepended INSIDE the root `<svg>` shell — keeping them
  // separate from the stash's docDefs means stash-restored
  // elements continue to reference their original paint-server
  // ids (no collision risk because the editor's `uniqueId` /
  // `usedIds` set has already reserved every layer's id).
  const stashDefs = originalSvg ? extractDocLevelDecls(originalSvg) : '';
  const editorDefs = gradientCollector.serialize();
  const rootAttrs = originalSvg
    ? extractRootAttrs(originalSvg, canvasWidth, canvasHeight)
    : defaultRootAttrs(canvasWidth, canvasHeight);
  const svgText =
    `<?xml version="1.0" encoding="UTF-8"?>\n` +
    `<svg ${rootAttrs}>${stashDefs}${editorDefs}${body}</svg>`;

  return { svgText, summary };
}

/** Extract the root `<svg>`'s namespace + viewBox + width/height
 *  attributes from the stash, so the output preserves the original
 *  coordinate system. Falls back to defaults when missing. Width and
 *  height are overridden to the editor's canvas dimensions because
 *  the original may carry relative units (`width="100%"`) or
 *  obsolete pixel sizes that don't match the current canvas — both
 *  would cause the saved file to render at wrong dimensions in
 *  other viewers. The viewBox is preserved unchanged (it defines
 *  the coordinate system, not the rendered size). Pre-PR review
 *  finding (R1#4). */
function extractRootAttrs(
  originalSvg: XMLDocument,
  canvasWidth: number,
  canvasHeight: number,
): string {
  const root = originalSvg.documentElement;
  if (!root) return defaultRootAttrs(canvasWidth, canvasHeight);
  const attrs: string[] = [];
  let hasXmlns = false;
  for (let i = 0; i < root.attributes.length; i++) {
    const a = root.attributes[i];
    if (a.name === 'xmlns') hasXmlns = true;
    // Skip width/height — re-emitted with canvas dimensions below.
    if (a.name === 'width' || a.name === 'height') continue;
    attrs.push(`${a.name}="${escapeAttr(a.value)}"`);
  }
  if (!hasXmlns) attrs.unshift('xmlns="http://www.w3.org/2000/svg"');
  attrs.push(`width="${canvasWidth}"`);
  attrs.push(`height="${canvasHeight}"`);
  return attrs.join(' ');
}

/** Extract the attribute span from a serialized empty `<g>` element.
 *  Inputs can be either `<g attrs/>` or `<g attrs></g>`; in both
 *  cases the attributes are everything between `<g ` and either
 *  `/>` or `>`. Returns the attribute span INCLUDING the leading
 *  space (so the caller can concatenate `<g${span}>...`). DOM-based
 *  extraction avoids the regex fragility that earlier versions had
 *  on string-manipulated group emission (R1#6 / R2#2). */
function extractGroupAttrSpan(serializedEmptyGroup: string): string {
  // Match `<g` optionally namespaced (`<svg:g`), capture the attr
  // body up to `/>` or `>`. Tolerate both self-closing and
  // open-close forms.
  const match = /^<[A-Za-z][\w:-]*\s?([\s\S]*?)\s*\/?>/m.exec(serializedEmptyGroup);
  const body = match?.[1] ?? '';
  return body ? ` ${body.trim()}` : '';
}

function defaultRootAttrs(width: number, height: number): string {
  return `xmlns="http://www.w3.org/2000/svg" width="${width}" height="${height}" viewBox="0 0 ${width} ${height}"`;
}

/** Serialize the original SVG's document-level declarations: `<defs>`,
 *  `<style>`, `<title>`, `<desc>`, `<metadata>` — every direct child
 *  of `<svg>` that doesn't represent a layer. These get prepended to
 *  the output's body so paint-server refs (`url(#gradient1)`) inside
 *  stash-restored elements continue to resolve. */
function extractDocLevelDecls(originalSvg: XMLDocument): string {
  const root = originalSvg.documentElement;
  if (!root) return '';
  const serializer = new XMLSerializer();
  const parts: string[] = [];
  for (const child of Array.from(root.children)) {
    const tag = child.localName.toLowerCase();
    if (SVG_WALK_SKIP_TAGS.has(tag)) {
      parts.push(serializer.serializeToString(child));
    }
  }
  return parts.join('');
}

// ---------- Bytes-out orchestrator ----------------------------------------

export interface ExportDocumentToSvgOptions {
  documentId: string;
  document: EditorDocument;
  canvasWidth: number;
  canvasHeight: number;
  layerCanvasProvider: LayerCanvasProvider;
  /** Forwarded to `buildExportSvg` — see that function's docstring
   *  for native `<text>` semantics. Optional; when omitted, text
   *  layers route through the existing raster fallback. */
  layerTextProvider?: LayerTextProvider;
}

export interface ExportDocumentToSvgResult {
  /** SVG bytes (UTF-8) ready for download / Fias upload / etc. */
  bytes: ArrayBuffer;
  /** Per-layer summary surfaced to the user via the post-save toast. */
  summary: SvgExportSummary;
  /** True when a source-stash was available and used. False when
   *  synthesizing from scratch. */
  hadStash: boolean;
  /** True when the stash existed but failed to re-parse (corrupted
   *  IDB record, truncated bytes, etc.). The export fell back to
   *  no-stash mode and emitted every layer editor-side. Caller
   *  should surface this to the user — silent fallback would
   *  misrepresent the lossless-round-trip claim. Pre-PR review
   *  finding (R1#2 / R3#A.6). */
  stashCorrupted: boolean;
  /** True when ANY rasterized stash-restored layer's serialized
   *  fragment contains an external `<image href="https://...">` or
   *  similar URL reference that would re-fetch when the saved file
   *  is opened. The original SVG text is preserved verbatim in the
   *  stash (privacy semantics differ from the import-side
   *  rasterizer's sanitizer) — recipients of the saved file would
   *  trigger fetches to those URLs. Caller surfaces a warning so
   *  the user can decide whether to share the file. Pre-PR review
   *  finding (R3#B). */
  containsExternalRefs: boolean;
}

/** Bytes-out entry point. Loads the stash, re-parses the original
 *  SVG text via DOMParser, runs the merge algorithm, returns bytes. */
export async function exportDocumentToSvg(
  opts: ExportDocumentToSvgOptions,
): Promise<ExportDocumentToSvgResult> {
  const {
    documentId,
    document,
    canvasWidth,
    canvasHeight,
    layerCanvasProvider,
    layerTextProvider,
  } = opts;

  const stash = await loadSvgStash(documentId);

  let originalSvg: XMLDocument | null = null;
  let stashCorrupted = false;
  if (stash) {
    const parsed = new DOMParser().parseFromString(stash.svgText, 'image/svg+xml');
    // DOMParser doesn't throw on malformed XML — it returns a doc
    // containing a `<parsererror>` element. Detect explicitly so
    // the caller can surface a warning rather than silently falling
    // back to all-raster emission. Pre-PR review finding (R1#2).
    const parserError = parsed.getElementsByTagName('parsererror')[0];
    if (parserError) {
      stashCorrupted = true;
      originalSvg = null;
    } else {
      originalSvg = parsed;
    }
  }

  const { svgText, summary } = buildExportSvg({
    document,
    originalSvg,
    layerOriginById: stashCorrupted ? null : (stash?.layerOriginById ?? null),
    layerCanvasProvider,
    layerTextProvider,
    canvasWidth,
    canvasHeight,
  });

  // Privacy scan — look for external URL references in the output
  // (covers <image href="https://...">, xlink:href, url(https://...)
  // in style attrs / inline CSS). Inexpensive regex pass over the
  // already-serialized output. Caller surfaces a warning toast.
  const containsExternalRefs =
    /(?:href|xlink:href)\s*=\s*["'](?:https?:|ftp:|\/\/)/i.test(svgText) ||
    /url\s*\(\s*["']?(?:https?:|ftp:|\/\/)/i.test(svgText);

  const bytes = new TextEncoder().encode(svgText).buffer as ArrayBuffer;
  return { bytes, summary, hadStash: stash !== null, stashCorrupted, containsExternalRefs };
}
